import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { User, Role } from '../../types';
import { getUser, updateUser, getRoles } from '../../utils/auth/authService';

const UserEditPage: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    try {
      const [userData, rolesData] = await Promise.all([
        getUser(id),
        getRoles()
      ]);
      setUser(userData);
      setRoles(rolesData);
      setSelectedRoles(userData.roles);
    } catch (err) {
      setError('Failed to load user');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id || !user) return;
    
    setSaving(true);
    setError('');

    try {
      await updateUser(id, { roles: selectedRoles });
      navigate('/auth/users');
    } catch (err) {
      setError('Failed to update user');
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/auth/users');
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: theme.text, marginBottom: 16 }}>
          User not found
        </div>
        <button
          onClick={() => navigate('/auth/users')}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/auth/users')}
            style={{
              padding: 8,
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>
              Edit User Roles
            </h1>
            <p style={{ color: theme.textSecondary, fontSize: 14, margin: '4px 0 0 0' }}>
              {user.username} ({user.email})
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleCancel}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 500,
              opacity: saving ? 0.5 : 1,
            }}
          >
            <X size={18} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: theme.buttonBackground,
              color: theme.buttonText,
              border: 'none',
              borderRadius: 4,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 500,
              opacity: saving ? 0.5 : 1,
            }}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
        }}>
          {error}
        </div>
      )}

      {/* User Info Card */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 20,
        marginBottom: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, margin: '0 0 16px 0' }}>
          User Information
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
        }}>
          <div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
              Username
            </div>
            <div style={{ color: theme.text, fontSize: 14, fontWeight: 500 }}>
              {user.username}
            </div>
          </div>
          <div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
              Email
            </div>
            <div style={{ color: theme.text, fontSize: 14, fontWeight: 500 }}>
              {user.email}
            </div>
          </div>
          <div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
              Status
            </div>
            <div style={{ color: user.is_active ? '#16a34a' : '#dc2626', fontSize: 14, fontWeight: 500 }}>
              {user.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
          <div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
              Auth Type
            </div>
            <div style={{ color: theme.text, fontSize: 14, fontWeight: 500 }}>
              {user.sso_provider_id ? 'SSO' : 'Local'}
            </div>
          </div>
        </div>
      </div>

      {/* Roles Section */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 20,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, margin: '0 0 16px 0' }}>
          Assign Roles
        </h2>
        <p style={{ color: theme.textSecondary, fontSize: 14, margin: '0 0 16px 0' }}>
          Select the roles to assign to this user. Users inherit all permissions from their assigned roles.
        </p>

        {roles.length === 0 ? (
          <div style={{
            padding: 20,
            textAlign: 'center',
            color: theme.textSecondary,
          }}>
            No roles available
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {roles.map(role => (
              <label
                key={role.role_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 12,
                  background: selectedRoles.includes(role.role_id) ? theme.hoverBackground : 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!selectedRoles.includes(role.role_id)) {
                    e.currentTarget.style.background = theme.background;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedRoles.includes(role.role_id)) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role.role_id)}
                  onChange={() => toggleRole(role.role_id)}
                  style={{
                    marginRight: 12,
                    width: 18,
                    height: 18,
                    cursor: 'pointer',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ 
                      color: theme.text, 
                      fontSize: 15, 
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}>
                      {role.name}
                    </span>
                    {role.is_system_role && (
                      <span style={{
                        padding: '2px 6px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                      }}>
                        SYSTEM
                      </span>
                    )}
                  </div>
                  {role.description && (
                    <div style={{ color: theme.textSecondary, fontSize: 13 }}>
                      {role.description}
                    </div>
                  )}
                  <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                    {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserEditPage;
